import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { API_BASE_URL } from '../config';
import Layout from '../components/Layout';
import { FaUser, FaClipboardCheck, FaProjectDiagram, FaMapMarkerAlt, FaCrosshairs, FaTimes, FaDirections } from 'react-icons/fa';
import ProfileTab from '../components/ProfileTab';

const MemberDashboard = () => {
    const [user, setUser] = useState(null);
    const [attendance, setAttendance] = useState([]);
    
    // Project States
    const [clubProjects, setClubProjects] = useState([]);
    const [nearestProjects, setNearestProjects] = useState([]);
    const [geoLoading, setGeoLoading] = useState(false);
    const [geoError, setGeoError] = useState('');
    
    // Tabs
    const [activeTab, setActiveTab] = useState('profile');
    const [activeProjectTab, setActiveProjectTab] = useState('club'); // 'club' or 'nearest'
    const [selectedProject, setSelectedProject] = useState(null);

    const navigate = useNavigate();

    const tabs = [
        { id: 'profile', label: 'My Profile', icon: <FaUser /> },
        { id: 'attendance', label: 'My Attendance', icon: <FaClipboardCheck /> },
        { id: 'projects', label: 'Projects', icon: <FaProjectDiagram /> },
    ];

    useEffect(() => {
        const storedUser = localStorage.getItem('user');
        if (!storedUser) {
            navigate('/login');
            return;
        }
        const userData = JSON.parse(storedUser);
        if (userData.role !== 'member') {
            if (!['super_admin', 'district_admin', 'club_admin'].includes(userData.role))
                navigate('/login');
        }
        setUser(userData);
        fetchData(userData);
    }, [navigate]);

    const fetchData = async (userData) => {
        try {
            // Fetch Attendance
            const resAtt = await fetch(`${API_BASE_URL}/member_actions.php`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify({ action: 'get_attendance' }),
            });
            const dataAtt = await resAtt.json();
            if (dataAtt.success) setAttendance(dataAtt.data.attendance);

            // Fetch Club Projects (Equivalent to legacy get_events)
            const resEvents = await fetch(`${API_BASE_URL}/member_actions.php`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify({ action: 'get_events' }),
            });
            const dataEvents = await resEvents.json();
            if (dataEvents.success) setClubProjects(dataEvents.data.events);

        } catch (error) {
            console.error('Error fetching data:', error);
        }
    };

    const fetchNearestProjects = async () => {
        if (!navigator.geolocation) {
            setGeoError("Geolocation is not supported by your browser");
            return;
        }
        
        setGeoLoading(true);
        setGeoError('');
        
        navigator.geolocation.getCurrentPosition(
            async (position) => {
                const lat = position.coords.latitude;
                const lng = position.coords.longitude;
                
                try {
                    const res = await fetch(`${API_BASE_URL}/member_actions.php`, {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        credentials: 'include',
                        body: JSON.stringify({ 
                            action: 'get_nearest_projects',
                            latitude: lat,
                            longitude: lng
                        }),
                    });
                    const data = await res.json();
                    if (data.success) {
                        setNearestProjects(data.data.projects);
                    } else {
                        setGeoError(data.message || "Failed to retrieve nearest projects");
                    }
                } catch (err) {
                    setGeoError("Network error while reaching servers");
                } finally {
                    setGeoLoading(false);
                }
            },
            (error) => {
                setGeoError("Unable to retrieve your location. Please grant permission.");
                setGeoLoading(false);
            }
        );
    };

    const handleProjectTabSwitch = (tab) => {
        setActiveProjectTab(tab);
        if (tab === 'nearest' && nearestProjects.length === 0 && !geoLoading) {
            fetchNearestProjects();
        }
    };

    const renderContent = () => {
        if (activeTab === 'profile') {
            return <ProfileTab user={user} />;
        }

        if (activeTab === 'attendance') {
            return (
                <div className="max-w-6xl mx-auto animate-fade-in">
                    <h2 className="text-3xl font-bold text-gray-900 mb-8 tracking-tight">My Attendance History</h2>
                    <div className="glass-card overflow-hidden">
                        {/* Desktop Table View */}
                        <div className="hidden md:block overflow-x-auto">
                            <table className="w-full text-left">
                                <thead className="bg-gray-50/50 text-gray-600 text-xs uppercase font-semibold">
                                    <tr>
                                        <th className="px-6 py-4">Project</th>
                                        <th className="px-6 py-4">Date</th>
                                        <th className="px-6 py-4">Club</th>
                                        <th className="px-6 py-4">Status</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-100">
                                    {attendance.length === 0 ? (
                                        <tr>
                                            <td colSpan="4" className="px-6 py-8 text-center text-gray-500">
                                                No attendance records found.
                                            </td>
                                        </tr>
                                    ) : (
                                        attendance.map((record) => (
                                            <tr key={record.id} className="hover:bg-gray-50">
                                                <td className="px-6 py-4 font-medium text-gray-900">
                                                    {record.project_name}
                                                    <div className="text-xs text-gray-500 font-normal">{record.description}</div>
                                                </td>
                                                <td className="px-6 py-4 text-gray-600">
                                                    {new Date(record.attendance_time).toLocaleDateString()}
                                                </td>
                                                <td className="px-6 py-4 text-gray-600">{record.club_name}</td>
                                                <td className="px-6 py-4">
                                                    <span className="bg-green-100 text-green-800 text-xs px-2 py-1 rounded-full font-medium">
                                                        {record.status}
                                                    </span>
                                                </td>
                                            </tr>
                                        ))
                                    )}
                                </tbody>
                            </table>
                        </div>

                        {/* Mobile Grid View */}
                        <div className="md:hidden divide-y divide-gray-100">
                            {attendance.length === 0 ? (
                                <div className="px-6 py-8 text-center text-gray-500">
                                    No attendance records found.
                                </div>
                            ) : (
                                attendance.map((record) => (
                                    <div key={record.id} className="p-4 space-y-3">
                                        <div className="flex justify-between items-start">
                                            <div>
                                                <div className="font-bold text-gray-900">{record.project_name}</div>
                                                <div className="text-xs text-gray-500 mt-1">{record.club_name}</div>
                                            </div>
                                            <span className="bg-green-100 text-green-800 text-[10px] px-2 py-1 rounded-full font-bold uppercase tracking-wider">
                                                {record.status}
                                            </span>
                                        </div>
                                        <div className="flex justify-between items-center pt-2 border-t border-gray-50">
                                            <div className="text-[10px] font-black text-gray-400 uppercase tracking-widest">
                                                {new Date(record.attendance_time).toLocaleDateString()}
                                            </div>
                                            {record.description && (
                                                <div className="text-[10px] text-gray-400 italic font-medium truncate max-w-[150px]">
                                                    {record.description}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                ))
                            )}
                        </div>
                    </div>
                </div>
            );
        }

        if (activeTab === 'projects') {
            const displayProjects = activeProjectTab === 'club' ? clubProjects : nearestProjects;
            
            return (
                <div className="max-w-6xl mx-auto animate-fade-in">
                    <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 gap-4">
                        <h2 className="text-3xl font-bold text-gray-900 tracking-tight">Project Discoveries</h2>
                        <div className="flex bg-gray-100 p-1.5 rounded-xl self-stretch sm:self-auto">
                            <button
                                onClick={() => handleProjectTabSwitch('club')}
                                className={`flex-1 sm:flex-none px-6 py-2.5 rounded-lg text-xs font-bold uppercase tracking-widest transition-all ${activeProjectTab === 'club' ? 'bg-white text-indigo-600 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
                            >
                                My Club
                            </button>
                            <button
                                onClick={() => handleProjectTabSwitch('nearest')}
                                className={`flex-1 sm:flex-none px-6 py-2.5 rounded-lg text-xs font-bold uppercase tracking-widest transition-all ${activeProjectTab === 'nearest' ? 'bg-white text-indigo-600 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
                            >
                                Nearest
                            </button>
                        </div>
                    </div>

                    {activeProjectTab === 'nearest' && (
                        <div className="mb-6 bg-indigo-50/50 p-6 rounded-2xl border border-indigo-100 flex items-center justify-between">
                            <div>
                                <h3 className="text-lg font-bold text-indigo-900 mb-1 flex items-center gap-2">
                                    <FaMapMarkerAlt className="text-indigo-500" /> Geography Scanner
                                </h3>
                                <p className="text-xs text-indigo-700/70 font-medium">Allow GPS tracking to find active Leo projects physically near your current location.</p>
                                {geoError && <p className="text-xs text-red-500 mt-2 font-bold">{geoError}</p>}
                            </div>
                            <button 
                                onClick={fetchNearestProjects} 
                                disabled={geoLoading}
                                className="bg-indigo-600 hover:bg-indigo-700 text-white px-5 py-3 rounded-xl font-bold text-xs uppercase tracking-widest transition-all flex items-center gap-2 disabled:opacity-50"
                            >
                                <FaCrosshairs /> {geoLoading ? 'Scanning Area...' : 'Rescan Location'}
                            </button>
                        </div>
                    )}

                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {displayProjects.length === 0 && !geoLoading && (
                            <div className="col-span-full text-center py-16 text-gray-400 font-bold uppercase tracking-widest text-xs bg-white rounded-3xl border border-dashed border-gray-200">
                                No {activeProjectTab === 'nearest' ? 'nearby' : 'club'} projects detected.
                            </div>
                        )}
                        
                        {geoLoading && activeProjectTab === 'nearest' && (
                            <div className="col-span-full flex flex-col items-center justify-center py-16 bg-white rounded-3xl border border-gray-100">
                                <div className="w-8 h-8 rounded-full border-4 border-indigo-100 border-t-indigo-600 animate-spin mb-4"></div>
                                <span className="text-xs font-bold text-gray-400 uppercase tracking-widest">Triangulating Position...</span>
                            </div>
                        )}

                        {!geoLoading && displayProjects.map((project) => (
                            <div 
                                key={project.id} 
                                onClick={() => setSelectedProject(project)}
                                className="glass-card p-6 flex flex-col justify-between group hover:-translate-y-1 hover:shadow-xl transition-all duration-300 relative overflow-hidden cursor-pointer"
                            >
                                {project.distance !== undefined && (
                                    <div className="absolute top-0 right-0 bg-indigo-500 text-white text-[10px] font-black tracking-widest px-4 py-1.5 rounded-bl-xl z-10">
                                        {parseFloat(project.distance).toFixed(1)} KM AWAY
                                    </div>
                                )}
                                <div>
                                    <div className="flex justify-between items-start mb-4">
                                        <h3 className="font-bold text-lg text-gray-900 line-clamp-2 leading-tight pr-12">{project.name}</h3>
                                        {!project.distance && (
                                            <span className="text-[10px] bg-slate-100 text-gray-500 font-black uppercase tracking-widest px-2 py-1 rounded-lg shrink-0">
                                                {new Date(project.created_at).toLocaleDateString()}
                                            </span>
                                        )}
                                    </div>
                                    <p className="text-gray-500 text-xs mb-6 line-clamp-3 leading-relaxed font-medium">
                                        {project.description || 'No operational description available.'}
                                    </p>
                                </div>
                                <div className="flex items-center gap-2 text-[10px] font-black text-gray-400 uppercase tracking-widest border-t border-gray-100 pt-5">
                                    <span className="text-indigo-600">{project.club_name}</span>
                                    <span>•</span>
                                    <span>{project.sub_district_name}</span>
                                    {project.latitude && project.longitude && !project.distance && (
                                        <FaMapMarkerAlt className="ml-auto text-indigo-400" title="Has Geolocation" />
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            );
        }
    };

    return (
        <Layout user={user} activeTab={activeTab} setActiveTab={setActiveTab} tabs={tabs}>
            {renderContent()}

            {/* Project Details Modal */}
            {selectedProject && (
                <div className="fixed inset-0 z-[100000] flex items-center justify-center p-4 bg-slate-900/40 backdrop-blur-sm animate-in fade-in duration-200" onClick={() => setSelectedProject(null)}>
                    <div className="bg-white rounded-3xl w-full max-w-lg overflow-hidden shadow-2xl transform transition-all animate-premium relative" onClick={e => e.stopPropagation()}>
                        <div className="p-6 border-b border-slate-100 flex justify-between items-start bg-slate-50/50">
                            <div>
                                <h3 className="text-xl font-black text-slate-900 uppercase tracking-tight pr-4">{selectedProject.name}</h3>
                                <p className="text-[10px] font-bold text-slate-500 uppercase tracking-widest mt-2">{selectedProject.club_name} • {selectedProject.sub_district_name}</p>
                            </div>
                            <button onClick={() => setSelectedProject(null)} className="w-10 h-10 flex shrink-0 items-center justify-center rounded-2xl bg-white text-slate-400 hover:text-slate-900 shadow-sm border border-slate-100 hover:bg-slate-50 transition-all">
                                <FaTimes size={16} />
                            </button>
                        </div>
                        <div className="p-8 space-y-6">
                            <div>
                                <h4 className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2">Project Brief</h4>
                                <p className="text-sm text-slate-700 leading-relaxed font-medium">{selectedProject.description || 'No operational description available.'}</p>
                            </div>
                            
                            <div className="grid grid-cols-2 gap-4">
                                <div className="p-4 bg-slate-50 rounded-2xl border border-slate-100">
                                    <h4 className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">Created On</h4>
                                    <p className="text-xs font-bold text-slate-900">{new Date(selectedProject.created_at).toLocaleDateString()}</p>
                                </div>
                                {selectedProject.distance !== undefined && (
                                    <div className="p-4 bg-indigo-50 rounded-2xl border border-indigo-100">
                                        <h4 className="text-[10px] font-black text-indigo-400 uppercase tracking-widest mb-1">Distance</h4>
                                        <p className="text-xs font-bold text-indigo-900">{parseFloat(selectedProject.distance).toFixed(2)} Kilometers</p>
                                    </div>
                                )}
                            </div>

                            {(selectedProject.latitude && selectedProject.longitude) ? (
                                <button 
                                    onClick={() => window.open(`https://www.google.com/maps/dir/?api=1&destination=${selectedProject.latitude},${selectedProject.longitude}`, '_blank')}
                                    className="w-full flex items-center justify-center gap-2 py-4 bg-indigo-600 text-white rounded-2xl font-black text-[10px] uppercase tracking-widest shadow-lg shadow-indigo-600/20 hover:bg-indigo-700 hover:-translate-y-0.5 transition-all"
                                >
                                    <FaDirections size={16} /> Get Directions to Location
                                </button>
                            ) : (
                                <div className="p-4 bg-orange-50 rounded-2xl border border-orange-100 text-center">
                                    <p className="text-[10px] font-black text-orange-600 uppercase tracking-widest flex items-center justify-center gap-2">
                                        <FaMapMarkerAlt /> No exact GPS location mapped
                                    </p>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </Layout>
    );
};

export default MemberDashboard;

